// src/pages/accounting/SalaryMatrixNLPage.jsx
// NL (Rotterdam) salary matrix management — CAO for Architects (art. 20)
//
// Matrix axes:
//   Columns: schaal 1-10 (standard CAO) + up to 4 custom schalen
//   Rows:    salaristrede 0-9
// All amounts in EUR (stored natively in EUR, never in INR).
// Display conversion via useSettings() as with other monetary values.

import React, { useState, useMemo } from 'react';
import { Edit2, X, ChevronUp, ChevronDown, Pencil, Trash2, Plus } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  useFullScreenPage,
  BrioFullScreenButton,
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STANDARD_SCHALEN = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
const ALL_TREDES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const MAX_CUSTOM_COLUMNS = 4;
const MAX_CUSTOM_TREDES = 4;

// BRIO Architecture role labels per CAO schaal
const SCHAAL_ROLE_LABELS = {
  '1':  'Intern',
  '2':  'Junior Architect',
  '3':  'Junior Architect',
  '4':  'Medior Architect',
  '5':  'Medior Architect',
  '6':  'Project Architect',
  '7':  'Senior Architect',
  '8':  'Senior Architect',
  '9':  'Senior Architect',
  '10': 'Associate Architect',
};

// The CAO (art. 20) seed data — used when importing via paste.
// Each row: [trede, ...values for schalen 1-10 where defined]
// Values count from highest schaal downward when fewer than 10 exist.
const CAO_SEED = {
  '0':  { '1': 2823, '2': 2823, '3': 2900, '4': 3171, '5': 3472, '6': 3802, '7': 4163, '8': 4560, '9': 4995, '10': 5475 },
  '1':  { '1': 2879, '2': 2900, '3': 2989, '4': 3272, '5': 3581, '6': 3921, '7': 4295, '8': 4703, '9': 5154, '10': 5651 },
  '2':  { '1': 2955, '2': 2981, '3': 3081, '4': 3372, '5': 3691, '6': 4041, '7': 4427, '8': 4849, '9': 5315, '10': 5827 },
  '3':  { '1': 3032, '2': 3065, '3': 3171, '4': 3472, '5': 3802, '6': 4163, '7': 4560, '8': 4995, '9': 5475, '10': 6001 },
  '4':  { '1': 3108, '2': 3151, '3': 3264, '4': 3572, '5': 3909, '6': 4283, '7': 4692, '8': 5141, '9': 5638, '10': 6177 },
  '5':  {           '2': 3234, '3': 3355, '4': 3673, '5': 4019, '6': 4404, '7': 4823, '8': 5287, '9': 5794, '10': 6354 },
  '6':  {           '2': 3314, '3': 3444, '4': 3771, '5': 4130, '6': 4522, '7': 4956, '8': 5432, '9': 5956, '10': 6529 },
  '7':  {           '2': 3400, '3': 3539, '4': 3871, '5': 4239, '6': 4644, '7': 5088, '8': 5578, '9': 6115, '10': 6706 },
  '8':  {                      '3': 3629, '4': 3972, '5': 4348, '6': 4764, '7': 5221, '8': 5725, '9': 6276, '10': 6883 },
  '9':  {                      '3': 3720, '4': 4071, '5': 4457, '6': 4885, '7': 5354, '8': 5869, '9': 6435, '10': 7057 },
};

// ---------------------------------------------------------------------------
// CAO text paste parser
// Parses the standard CAO table format:
//   salaristrede 0  2823  2823  2900  ...
// Returns a matrix object {schaal: {trede: amount}}.
// ---------------------------------------------------------------------------
export function parseCAOText(text) {
  const matrix = {};
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  for (const line of lines) {
    // Match lines starting with "salaristrede" followed by a number
    const match = line.match(/^salaristrede\s+(\d+)\s+(.+)$/i);
    if (!match) continue;

    const trede = match[1];
    const rawValues = match[2]
      .split(/\s+/)
      .map(v => parseInt(v, 10))
      .filter(v => !isNaN(v) && v > 0);

    if (rawValues.length === 0) continue;

    // Determine starting schaal: values fill from right (schaal 10 always last)
    // e.g. 10 values → schalen 1-10; 9 values → schalen 2-10; 8 → 3-10
    const startSchaal = STANDARD_SCHALEN.length - rawValues.length + 1;

    rawValues.forEach((amount, idx) => {
      const schaal = String(startSchaal + idx);
      if (!matrix[schaal]) matrix[schaal] = {};
      matrix[schaal][trede] = amount;
    });
  }

  return matrix;
}

// ---------------------------------------------------------------------------
// Empty matrix factory
// ---------------------------------------------------------------------------
function emptyMatrix(customColumns = []) {
  const m = {};
  for (const s of STANDARD_SCHALEN) m[s] = {};
  for (const cc of customColumns) m[cc.id] = {};
  return m;
}

// ---------------------------------------------------------------------------
// Shared styles (mirrors MH_STYLES from SalaryMatrixPage)
// ---------------------------------------------------------------------------
const S = {
  th: {
    padding: '5px 8px',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '11px',
    color: 'var(--brio-muted, #64748b)',
    borderBottom: '2px solid var(--brio-border, #e2e8f0)',
    whiteSpace: 'nowrap',
  },
  thLeft: {
    padding: '5px 8px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '11px',
    color: 'var(--brio-muted, #64748b)',
    borderBottom: '2px solid var(--brio-border, #e2e8f0)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '3px 4px',
    borderBottom: '1px solid var(--brio-border, #f1f5f9)',
    verticalAlign: 'middle',
    textAlign: 'center',
    fontSize: '11px',
  },
  tdLeft: {
    padding: '3px 8px',
    borderBottom: '1px solid var(--brio-border, #f1f5f9)',
    verticalAlign: 'middle',
    fontSize: '11px',
    fontWeight: 500,
  },
  badge: (color) => ({
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
    background: color === 'green' ? '#dcfce7' : color === 'amber' ? '#fef3c7' : '#f1f5f9',
    color: color === 'green' ? '#166534' : color === 'amber' ? '#92400e' : '#475569',
  }),
  input: {
    width: '64px',
    textAlign: 'center',
    padding: '2px 4px',
    border: '1px solid var(--brio-border, #d1d5db)',
    borderRadius: '3px',
    fontSize: '11px',
    boxSizing: 'border-box',
  },
  warning: {
    background: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '12px',
    color: '#92400e',
    marginBottom: '8px',
  },
};

// ---------------------------------------------------------------------------
// MatrixGrid — transposed: rows = schaal (with role label), columns = trede
// ---------------------------------------------------------------------------
function MatrixGrid({ matrix, customColumns, customTredes, isEditing, onCellChange, onCustomNameChange, onCustomTredeNameChange }) {
  const allSchalen = [...STANDARD_SCHALEN, ...customColumns.map(c => c.id)];
  const allTredes = [...ALL_TREDES, ...customTredes.map(t => t.id)];

  const getSchaalLabel = (schaal) => {
    if (!STANDARD_SCHALEN.includes(schaal)) {
      const cc = customColumns.find(c => c.id === schaal);
      return { schaal: cc ? cc.name : schaal, role: null };
    }
    return { schaal: `Schaal ${schaal}`, role: SCHAAL_ROLE_LABELS[schaal] || null };
  };

  const getTredeLabel = (trede) => {
    if (ALL_TREDES.includes(trede)) return `Trede ${trede}`;
    const ct = customTredes.find(t => t.id === trede);
    return ct?.name || `Trede ${trede}`;
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '11px', minWidth: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...S.thLeft, position: 'sticky', left: 0, background: 'var(--brio-grey-light)', zIndex: 10, minWidth: '180px' }}>
              Schaal / Role
            </th>
            {allTredes.map(trede => {
              const isCustom = !ALL_TREDES.includes(trede);
              return (
                <th key={trede} style={{ ...S.th, minWidth: '68px', background: isCustom ? '#eff6ff' : undefined }}>
                  {isEditing && isCustom ? (
                    <input
                      value={customTredes.find(t => t.id === trede)?.name || ''}
                      onChange={e => onCustomTredeNameChange(trede, e.target.value)}
                      style={{ ...S.input, width: '60px', fontWeight: 600, fontSize: '10px' }}
                      placeholder={`Trede ${trede}`}
                    />
                  ) : (
                    <span>{getTredeLabel(trede)}</span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {allSchalen.map(s => {
            const { schaal: schaalLabel, role: roleLabel } = getSchaalLabel(s);
            return (
              <tr key={s}>
                <td style={{ ...S.tdLeft, position: 'sticky', left: 0, background: '#fff', zIndex: 5, borderRight: '1px solid var(--brio-border)' }}>
                  {isEditing && !STANDARD_SCHALEN.includes(s) ? (
                    <input
                      value={customColumns.find(c => c.id === s)?.name || ''}
                      onChange={e => onCustomNameChange(s, e.target.value)}
                      style={{ ...S.input, width: '120px', fontWeight: 600 }}
                      placeholder="Column name"
                    />
                  ) : (
                    <div>
                      <span style={{ fontWeight: 600 }}>{schaalLabel}</span>
                      {roleLabel && (
                        <span style={{ display: 'block', fontSize: '9px', color: 'var(--brio-muted)', fontWeight: 400, marginTop: '1px' }}>
                          {roleLabel}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                {allTredes.map(trede => {
                  const val = matrix?.[s]?.[trede];
                  const isCustomTrede = !ALL_TREDES.includes(trede);
                  return (
                    <td key={trede} style={{ ...S.td, background: isCustomTrede ? '#f8fbff' : undefined }}>
                      {isEditing ? (
                        <input
                          type="number"
                          value={val || ''}
                          onChange={e => onCellChange(s, trede, parseFloat(e.target.value) || 0)}
                          style={S.input}
                          placeholder="—"
                          title={`${schaalLabel}, ${getTredeLabel(trede)} — monthly EUR`}
                        />
                      ) : (
                        val
                          ? <span style={{ color: '#1e293b' }}>{val.toLocaleString('nl-NL')}</span>
                          : <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
export default function SalaryMatrixNLPage({
  salaryMatrixHistoryNL,
  setSalaryMatrixHistoryNL,
  currentUser,
  useSupabaseDB,
  supabaseSalaryMatrixNL,
}) {
  const { formatMoney } = useSettings();

  // ── Active matrix (most recent revision covering today) ──────────────────
  const today = new Date().toISOString().split('T')[0];
  const currentRevision = useMemo(() => {
    const sorted = [...salaryMatrixHistoryNL].sort((a, b) =>
      b.effectiveDate.localeCompare(a.effectiveDate)
    );
    return sorted.find(h =>
      h.effectiveDate <= today && (!h.endDate || h.endDate >= today)
    ) || sorted[0] || null;
  }, [salaryMatrixHistoryNL, today]);

  const currentMatrix = currentRevision?.matrix || emptyMatrix();
  const currentCustomColumns = currentRevision?.customColumns || [];
  const currentCustomTredes = currentRevision?.customTredes || [];

  // ── Edit mode ────────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [tempMatrix, setTempMatrix] = useState(null);
  const [tempCustomColumns, setTempCustomColumns] = useState([]);
  const [tempCustomTredes, setTempCustomTredes] = useState([]);
  const [editForm, setEditForm] = useState({ effectiveDate: '', endDate: '', changeReason: '' });
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Import state ─────────────────────────────────────────────────────────
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importError, setImportError] = useState(null);

  // ── History panel ────────────────────────────────────────────────────────
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [editingRevision, setEditingRevision] = useState(null);
  const [revFormData, setRevFormData] = useState({});
  const [revFormError, setRevFormError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const startEditing = () => {
    setTempMatrix(JSON.parse(JSON.stringify(currentMatrix)));
    setTempCustomColumns(JSON.parse(JSON.stringify(currentCustomColumns)));
    setTempCustomTredes(JSON.parse(JSON.stringify(currentCustomTredes)));
    setEditForm({ effectiveDate: today, endDate: '', changeReason: '' });
    setEditError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTempMatrix(null);
    setTempCustomColumns([]);
    setTempCustomTredes([]);
    setEditError(null);
    setShowImport(false);
    setImportText('');
    setImportPreview(null);
  };

  const handleCellChange = (schaal, trede, value) => {
    setTempMatrix(prev => {
      const next = { ...prev, [schaal]: { ...(prev[schaal] || {}) } };
      if (value === 0) {
        delete next[schaal][trede];
      } else {
        next[schaal][trede] = value;
      }
      return next;
    });
  };

  const handleCustomNameChange = (id, name) => {
    setTempCustomColumns(prev => prev.map(c => c.id === id ? { ...c, name } : c));
  };

  const addCustomColumn = () => {
    if (tempCustomColumns.length >= MAX_CUSTOM_COLUMNS) return;
    const id = `c_${tempCustomColumns.length + 1}`;
    setTempCustomColumns(prev => [...prev, { id, name: `Custom ${prev.length + 1}` }]);
    setTempMatrix(prev => ({ ...prev, [id]: {} }));
  };

  const removeCustomColumn = (id) => {
    setTempCustomColumns(prev => prev.filter(c => c.id !== id));
    setTempMatrix(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const addCustomTrede = () => {
    if (tempCustomTredes.length >= MAX_CUSTOM_TREDES) return;
    const nextNum = ALL_TREDES.length + tempCustomTredes.length;
    const id = String(nextNum);
    setTempCustomTredes(prev => [...prev, { id, name: `Trede ${nextNum}` }]);
  };

  const removeCustomTrede = (id) => {
    setTempCustomTredes(prev => prev.filter(t => t.id !== id));
    // Remove trede values from all schalen in the matrix
    setTempMatrix(prev => {
      const next = {};
      for (const [schaal, tredes] of Object.entries(prev)) {
        const { [id]: _, ...rest } = tredes;
        next[schaal] = rest;
      }
      return next;
    });
  };

  const handleCustomTredeNameChange = (id, name) => {
    setTempCustomTredes(prev => prev.map(t => t.id === id ? { ...t, name } : t));
  };

  // ── CAO import ───────────────────────────────────────────────────────────
  const handleImportPreview = () => {
    setImportError(null);
    try {
      const parsed = parseCAOText(importText);
      const count = Object.keys(parsed).length;
      if (count === 0) {
        setImportError('No valid "salaristrede" rows found. Check the pasted text.');
        return;
      }
      setImportPreview(parsed);
    } catch {
      setImportError('Failed to parse the pasted text.');
    }
  };

  const handleImportApply = () => {
    if (!importPreview) return;
    // Merge parsed schalen into temp matrix (preserve custom columns)
    setTempMatrix(prev => {
      const next = { ...prev };
      for (const [schaal, tredes] of Object.entries(importPreview)) {
        next[schaal] = { ...(next[schaal] || {}), ...tredes };
      }
      return next;
    });
    setShowImport(false);
    setImportText('');
    setImportPreview(null);
  };

  // ── Save new revision ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editForm.effectiveDate) {
      setEditError('Start Date is required.');
      return;
    }
    setSaving(true);
    setEditError(null);

    const payload = {
      effectiveDate:  editForm.effectiveDate,
      endDate:        editForm.endDate || null,
      changedBy:      currentUser?.email,
      changeReason:   editForm.changeReason || 'CAO matrix update',
      matrix:         tempMatrix,
      customColumns:  tempCustomColumns,
      customTredes:   tempCustomTredes,
    };

    if (useSupabaseDB) {
      const result = await supabaseSalaryMatrixNL.createRevision(payload);
      setSaving(false);
      if (result.success) {
        setSalaryMatrixHistoryNL(result.history);
        cancelEditing();
      } else {
        setEditError(result.error || 'Save failed.');
      }
    } else {
      setSalaryMatrixHistoryNL(prev => [
        { ...payload, id: Date.now(), createdAt: new Date().toISOString() },
        ...prev,
      ]);
      setSaving(false);
      cancelEditing();
    }
  };

  // ── Revision history: edit ───────────────────────────────────────────────
  const openRevEdit = (rev) => {
    setEditingRevision(rev);
    setRevFormData({
      effectiveDate:  rev.effectiveDate,
      endDate:        rev.endDate || '',
      changeReason:   rev.changeReason || '',
      matrix:         rev.matrix         ? JSON.parse(JSON.stringify(rev.matrix))         : {},
      customColumns:  rev.customColumns  ? JSON.parse(JSON.stringify(rev.customColumns))  : [],
      customTredes:   rev.customTredes   ? JSON.parse(JSON.stringify(rev.customTredes))   : [],
    });
    setRevFormError(null);
  };

  const handleRevCellChange = (schaal, trede, value) => {
    setRevFormData(prev => {
      const matrix = { ...prev.matrix, [schaal]: { ...(prev.matrix?.[schaal] || {}) } };
      if (value === 0) {
        delete matrix[schaal][trede];
      } else {
        matrix[schaal][trede] = value;
      }
      return { ...prev, matrix };
    });
  };

  const handleRevSave = async () => {
    if (!revFormData.effectiveDate) { setRevFormError('Start Date required.'); return; }
    setSaving(true);
    const payload = {
      effectiveDate:  revFormData.effectiveDate,
      endDate:        revFormData.endDate || null,
      changedBy:      currentUser?.email,
      changeReason:   revFormData.changeReason || 'Update',
      matrix:         revFormData.matrix,
      customColumns:  revFormData.customColumns,
      customTredes:   revFormData.customTredes,
    };
    if (useSupabaseDB) {
      const result = await supabaseSalaryMatrixNL.updateRevision(editingRevision.id, payload);
      setSaving(false);
      if (result.success) { setSalaryMatrixHistoryNL(result.history); setEditingRevision(null); }
      else setRevFormError(result.error || 'Update failed.');
    } else {
      setSalaryMatrixHistoryNL(prev => prev.map(h => h.id === editingRevision.id ? { ...h, ...payload } : h));
      setSaving(false);
      setEditingRevision(null);
    }
  };

  const handleRevDelete = async (id) => {
    setSaving(true);
    if (useSupabaseDB) {
      const result = await supabaseSalaryMatrixNL.deleteRevision(id);
      setSaving(false);
      setConfirmDeleteId(null);
      if (result.success) setSalaryMatrixHistoryNL(result.history);
      else alert('Delete failed: ' + (result.error || 'Unknown error'));
    } else {
      setSalaryMatrixHistoryNL(prev => prev.filter(h => h.id !== id));
      setSaving(false);
      setConfirmDeleteId(null);
    }
  };

  // ── Active display matrix ────────────────────────────────────────────────
  const displayMatrix   = isEditing ? tempMatrix         : currentMatrix;
  const displayCustomCC = isEditing ? tempCustomColumns  : currentCustomColumns;
  const displayCustomTR = isEditing ? tempCustomTredes   : currentCustomTredes;

  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>
      {/* PAGE HEADER */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">
              <p className="brio-analytics-muted brio-mt-xs">
                NL office salary matrix (Rotterdam). Based on CAO voor Architectenbureaus art. 20.
                Amounts in <strong>EUR</strong>.
              </p>
            </div>
            <div className="brio-page-header__right">
              {!isEditing ? (
                <button onClick={startEditing} className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs">
                  <Edit2 className="w-4 h-4" /> Edit / New Version
                </button>
              ) : (
                <div className="brio-flex brio-flex-col brio-gap-s">
                  <div className="brio-flex brio-items-center" style={{ flexWrap: 'wrap', gap: '6px' }}>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '2px' }}>Start Date *</label>
                      <input
                        type="date"
                        value={editForm.effectiveDate}
                        onChange={e => setEditForm(f => ({ ...f, effectiveDate: e.target.value }))}
                        style={{ fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border)', borderRadius: 0, width: '100px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '2px' }}>End Date (opt.)</label>
                      <input
                        type="date"
                        value={editForm.endDate}
                        onChange={e => setEditForm(f => ({ ...f, endDate: e.target.value }))}
                        style={{ fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border)', borderRadius: 0, width: '100px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 500, display: 'block', marginBottom: '2px' }}>Change Reason</label>
                      <input
                        type="text"
                        value={editForm.changeReason}
                        onChange={e => setEditForm(f => ({ ...f, changeReason: e.target.value }))}
                        placeholder="e.g., CAO 2026 update"
                        className="brio-btn brio-btn-secondary"
                        style={{ padding: '6px 10px' }}
                      />
                    </div>
                  </div>
                  {editError && <div style={S.warning}>{editError}</div>}
                  <div className="brio-flex brio-gap-xs">
                    <button onClick={handleSave} disabled={saving} className="brio-btn brio-btn-primary" style={{ background: 'var(--brio-green)' }}>
                      {saving ? 'Saving…' : '✓ Save Version'}
                    </button>
                    <button onClick={cancelEditing} className="brio-btn brio-btn-secondary">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} style={{ alignSelf: 'flex-start' }} />
          </div>
        </BrioFrame>
      </div>

      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>

          {/* Info banner */}
          <div style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)', padding: '12px 16px' }}>
            <p className="brio-analytics-body">
              💡 <strong>CAO art. 20 —</strong> Rows are schaal (1-10) with BRIO role labels. Columns are salaristrede (0-9, plus any custom tredes).
              Cells that are blank mean that trede does not exist for that schaal in the CAO.
              Amounts are gross monthly salary in <strong>EUR</strong>.
            </p>
          </div>

          {/* Edit mode: import + custom column controls */}
          {isEditing && (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-flex brio-items-center brio-gap-s" style={{ flexWrap: 'wrap' }}>
                {/* CAO paste import */}
                <button
                  onClick={() => { setShowImport(!showImport); setImportError(null); }}
                  className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                >
                  {showImport ? '✕ Close Import' : '⬇ Import CAO Paste'}
                </button>

                {/* Custom columns */}
                {displayCustomCC.map(cc => (
                  <span key={cc.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>
                    {cc.name}
                    <button onClick={() => removeCustomColumn(cc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '0 2px', lineHeight: 1 }}>×</button>
                  </span>
                ))}
                {displayCustomCC.length < MAX_CUSTOM_COLUMNS && (
                  <button onClick={addCustomColumn} className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" style={{ fontSize: '11px' }}>
                    <Plus size={12} /> Add Custom Column
                  </button>
                )}

                {/* Custom tredes */}
                <span style={{ borderLeft: '1px solid var(--brio-border)', height: '20px', margin: '0 4px' }} />
                {displayCustomTR.map(ct => (
                  <span key={ct.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>
                    {ct.name}
                    <button onClick={() => removeCustomTrede(ct.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '0 2px', lineHeight: 1 }}>×</button>
                  </span>
                ))}
                {displayCustomTR.length < MAX_CUSTOM_TREDES && (
                  <button onClick={addCustomTrede} className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" style={{ fontSize: '11px' }}>
                    <Plus size={12} /> Add Custom Trede
                  </button>
                )}
              </div>

              {/* Import panel */}
              {showImport && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', border: '1px solid var(--brio-border)', borderRadius: '6px' }}>
                  <p style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Paste CAO art. 20 table text</p>
                  <p style={{ fontSize: '11px', color: 'var(--brio-muted)', marginBottom: '8px' }}>
                    Paste directly from the CAO document. Each row should start with "salaristrede" followed by the trede number and salary values.
                  </p>
                  <textarea
                    value={importText}
                    onChange={e => { setImportText(e.target.value); setImportPreview(null); }}
                    rows={12}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '11px', padding: '8px', border: '1px solid var(--brio-border)', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
                    placeholder={`salaristrede 0 2823 2823 2900 3171 3472 3802 4163 4560 4995 5475\nsalaristrede 1 2879 2900 2989 3272 3581 3921 4295 4703 5154 5651\n...`}
                  />
                  {importError && <div style={{ ...S.warning, marginTop: '6px' }}>{importError}</div>}
                  <div className="brio-flex brio-gap-xs" style={{ marginTop: '8px' }}>
                    <button onClick={handleImportPreview} className="brio-btn brio-btn-secondary" style={{ fontSize: '11px' }}>
                      Preview Parse
                    </button>
                    {importPreview && (
                      <button onClick={handleImportApply} className="brio-btn brio-btn-primary" style={{ background: 'var(--brio-green)', fontSize: '11px' }}>
                        ✓ Apply to Matrix ({Object.keys(importPreview).length} schalen parsed)
                      </button>
                    )}
                  </div>
                  {importPreview && (
                    <p style={{ fontSize: '11px', color: '#166534', marginTop: '6px' }}>
                      ✓ Parsed {Object.keys(importPreview).length} schalen successfully. Click "Apply" to update the matrix below.
                    </p>
                  )}
                </div>
              )}
            </BrioAnalyticsSurface>
          )}

          {/* Matrix grid */}
          <BrioAnalyticsSurface padding="m">
            {!currentRevision && !isEditing && (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--brio-muted)', fontSize: '13px' }}>
                No NL salary matrix version created yet. Click "Edit / New Version" to add the first version.
              </div>
            )}
            {(currentRevision || isEditing) && (
              <MatrixGrid
                matrix={displayMatrix}
                customColumns={displayCustomCC}
                customTredes={displayCustomTR}
                isEditing={isEditing}
                onCellChange={handleCellChange}
                onCustomNameChange={handleCustomNameChange}
                onCustomTredeNameChange={handleCustomTredeNameChange}
              />
            )}
            <div style={{ marginTop: '10px', background: 'var(--brio-grey-light)', padding: '10px 12px', fontSize: '11px', color: 'var(--brio-muted)' }}>
              • Values are <strong>full-time gross monthly salary</strong> (176h/month) in <strong>EUR</strong>. Blank cells = trede not defined for that schaal per the CAO.
              <br />
              • Part-time employees with the same schaal/trede have the same hourly rate; their actual monthly cost is prorated by contracted hours.
              <br />
              • Assign schaal and trede to NL team members in their profile.
            </div>
          </BrioAnalyticsSurface>

          {/* Version history */}
          <BrioAnalyticsSurface padding="m">
            <button
              onClick={() => setHistoryExpanded(h => !h)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: 0, fontWeight: 600, fontSize: '14px', color: 'var(--brio-gold)', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              {historyExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              Matrix Version History
              <span style={{ fontWeight: 400, fontSize: '12px', textTransform: 'none', letterSpacing: 0, color: 'var(--brio-muted)' }}>
                ({salaryMatrixHistoryNL.length} {salaryMatrixHistoryNL.length === 1 ? 'version' : 'versions'})
              </span>
            </button>

            {historyExpanded && (
              <div style={{ marginTop: '12px' }}>
                {salaryMatrixHistoryNL.length === 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>
                    No versions yet. Create a matrix version above.
                  </div>
                )}
                {salaryMatrixHistoryNL.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr>
                        {['#', 'Start', 'End', 'Status', 'Changed By', 'Reason', 'Created', ''].map(h => (
                          <th key={h} style={{ ...S.thLeft, textAlign: h === '' ? 'right' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {salaryMatrixHistoryNL.map((rev, idx) => {
                        const isCurrent = rev.effectiveDate <= today && (!rev.endDate || rev.endDate >= today);
                        const isEditing_ = editingRevision?.id === rev.id;
                        return (
                          <tr key={rev.id} style={{ background: isCurrent ? '#fafaf6' : 'transparent' }}>
                            <td style={S.tdLeft}>{salaryMatrixHistoryNL.length - idx}</td>
                            <td style={S.tdLeft}>{rev.effectiveDate}</td>
                            <td style={S.tdLeft}>{rev.endDate || <span style={{ color: 'var(--brio-muted)', fontStyle: 'italic' }}>open</span>}</td>
                            <td style={S.tdLeft}>
                              {isCurrent
                                ? <span style={S.badge('green')}>Active</span>
                                : rev.effectiveDate > today
                                  ? <span style={S.badge('amber')}>Upcoming</span>
                                  : <span style={S.badge('default')}>Superseded</span>
                              }
                            </td>
                            <td style={S.tdLeft}>{rev.changedBy || '—'}</td>
                            <td style={S.tdLeft}>{rev.changeReason || '—'}</td>
                            <td style={S.tdLeft}>{rev.createdAt ? new Date(rev.createdAt).toLocaleDateString() : '—'}</td>
                            <td style={{ ...S.tdLeft, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {confirmDeleteId === rev.id ? (
                                <span style={{ fontSize: '11px' }}>
                                  Delete?{' '}
                                  <button onClick={() => handleRevDelete(rev.id)} disabled={saving} style={{ color: '#dc2626', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}>Yes</button>
                                  {' / '}
                                  <button onClick={() => setConfirmDeleteId(null)} style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}>No</button>
                                </span>
                              ) : isEditing_ ? (
                                <span style={{ fontSize: '11px', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                                  <span style={{ color: 'var(--brio-gold)', fontWeight: 500 }}>editing…</span>
                                  <button onClick={() => setEditingRevision(null)} style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                                </span>
                              ) : (
                                <>
                                  <button onClick={() => openRevEdit(rev)} title="Edit dates" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--brio-gold)' }}>
                                    <Pencil size={13} />
                                  </button>
                                  <button onClick={() => setConfirmDeleteId(rev.id)} title="Delete version" disabled={isCurrent && salaryMatrixHistoryNL.length === 1} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: isCurrent && salaryMatrixHistoryNL.length === 1 ? '#d1d5db' : '#dc2626' }}>
                                    <Trash2 size={13} />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Full edit panel — shown below the table when a revision is being edited */}
                {editingRevision && (
                  <div style={{ marginTop: '12px', padding: '16px', background: '#fafaf6', border: '1px solid var(--brio-border, #e2e8f0)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: 'var(--brio-text, #1e293b)' }}>
                      Edit Version #{salaryMatrixHistoryNL.length - salaryMatrixHistoryNL.findIndex(h => h.id === editingRevision.id)}
                    </div>

                    {revFormError && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#991b1b', marginBottom: '8px' }}>
                        {revFormError}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-muted)' }}>Start Date *</label>
                        <input
                          type="date"
                          value={revFormData.effectiveDate || ''}
                          onChange={e => setRevFormData(f => ({ ...f, effectiveDate: e.target.value }))}
                          style={{ fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border)', borderRadius: 0, width: '100px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-muted)' }}>End Date</label>
                        <input
                          type="date"
                          value={revFormData.endDate || ''}
                          onChange={e => setRevFormData(f => ({ ...f, endDate: e.target.value }))}
                          style={{ fontSize: '10px', padding: '3px 4px', border: '1px solid var(--brio-border)', borderRadius: 0, width: '100px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-muted)' }}>Change Reason</label>
                        <input
                          type="text"
                          value={revFormData.changeReason || ''}
                          onChange={e => setRevFormData(f => ({ ...f, changeReason: e.target.value }))}
                          placeholder="e.g., Correction"
                          style={{ padding: '3px 8px', border: '1px solid var(--brio-border)', borderRadius: '4px', fontSize: '12px', width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--brio-text, #1e293b)' }}>
                      Edit Salary Matrix
                    </div>
                    <MatrixGrid
                      matrix={revFormData.matrix || {}}
                      customColumns={revFormData.customColumns || []}
                      customTredes={revFormData.customTredes || []}
                      isEditing={true}
                      onCellChange={handleRevCellChange}
                      onCustomNameChange={(id, name) => setRevFormData(f => ({ ...f, customColumns: f.customColumns.map(c => c.id === id ? { ...c, name } : c) }))}
                      onCustomTredeNameChange={(id, name) => setRevFormData(f => ({ ...f, customTredes: f.customTredes.map(t => t.id === id ? { ...t, name } : t) }))}
                    />

                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setEditingRevision(null)}
                        disabled={saving}
                        style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '4px', background: '#fff', border: '1px solid var(--brio-border, #d1d5db)', color: 'var(--brio-text, #475569)', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRevSave}
                        disabled={saving}
                        style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '4px', background: 'var(--brio-gold, #9F902A)', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.6 : 1 }}
                      >
                        {saving ? 'Saving…' : 'Update'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
